/**
 * Industry Hero Component
 * 
 * This component provides a consistent hero section for industry pages with
 * full-width banner, optimized navigation visibility and product showcase.
 */
'use client';

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion, useScroll, useTransform, useReducedMotion, type UseScrollOptions } from 'framer-motion';
import { Badge, Heading, Text, Button } from '@/components/ui/design-system';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/adapters/Card';
import { ArrowRight, Award, ShieldCheck, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';



/**
 * Props for the IndustryHero component
 */
export interface IndustryHeroProps {
  /**
   * Main title for the hero section
   */
  title?: React.ReactNode;
  
  /**
   * Description text
   */
  description?: React.ReactNode;
  
  /**
   * Image URL for the hero background
   */
  imageUrl?: string;
  
  /**
   * Mobile image URL for the hero background
   */
  mobileImageUrl?: string;
  
  /**
   * List of features to display as pills
   */
  features?: ReadonlyArray<{
    icon: React.ReactNode;
    text: string;
  }>;

  /**
   * Primary CTA text
   */
  primaryCta?: string;
  
  /**
   * Primary CTA link
   */
  primaryCtaLink?: string;
  
  /**
   * Secondary CTA text
   */
  secondaryCta?: string;
  
  /**
   * Secondary CTA link
   */
  secondaryCtaLink?: string;
  
  /**
   * Additional class name
   */
  className?: string;
}

/**
 * Default features for the hero section - Memoized for performance
 */
const DEFAULT_FEATURES: ReadonlyArray<{ icon: React.ReactNode; text: string }> = [
  {
    icon: <Award className="text-primary-400 mr-2 h-4 w-4" />,
    text: 'Premium Quality'
  },
  {
    icon: <ShieldCheck className="text-primary-400 mr-2 h-4 w-4" />,
    text: 'Food-Safe Materials'
  },
  {
    icon: <TrendingUp className="text-primary-400 mr-2 h-4 w-4" />,
    text: 'Industry Leaders'
  }
];

/**
 * Industry Hero component
 * 
 * A sophisticated hero section for industry pages with elegant styling,
 * optimized for navigation visibility with a full-width banner that has
 * light gradient on the left for text contrast and product showcase on the right.
 * 
 * @example
 * ```tsx
 * <IndustryHero 
 *   title="Specialty Packaging Solutions for Every Industry"
 *   badgeText="Industries We Serve"
 * />
 * ```
 */
export default function IndustryHero({ 
  title = "Specialty Packaging Solutions for Every Industry",
  description = "Discover how our BPA-free bottles and food-safe PET packaging serve diverse industries with quality, safety, and innovation at the forefront.",
  imageUrl = "/images/pages/industries/hero/hero-banner.jpg",
  mobileImageUrl = "/images/pages/industries/hero/hero-banner-mobile.jpg",
  features = DEFAULT_FEATURES,
  primaryCta = "Get Custom Quote",
  primaryCtaLink = "/contact",
  secondaryCta = "Browse Products",
  secondaryCtaLink = "/products",
  className,
  ...props 
}: IndustryHeroProps) {
  const heroRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  
  // Memoize scroll configuration to prevent recreation
  const scrollConfig = useMemo<UseScrollOptions>(() => ({
    target: heroRef,
    offset: ["start start", "end start"]
  }), []);
  
  const { scrollYProgress } = useScroll(scrollConfig);
  
  // Memoize transform configurations
  const bannerY = useTransform(scrollYProgress, [0, 1], [0, prefersReducedMotion ? 0 : 100]);
  const opacityProgress = useTransform(scrollYProgress, [0, 0.5], [1, 0.5]);
  
  // Mouse parallax effect - optimized with useCallback and throttling
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  const handleMouseMove = useCallback((e: MouseEvent) => {
    // Skip if reduced motion is preferred or on smaller screens
    if (prefersReducedMotion || window.innerWidth <= 1024) return;
    
    const { clientX, clientY } = e;
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    
    // Calculate movement with reduced intensity for performance
    const moveX = (clientX - centerX) / centerX * 5; // Reduced from 10px to 5px
    const moveY = (clientY - centerY) / centerY * 5; // Reduced from 10px to 5px
    
    setMousePosition({ x: moveX, y: moveY });
  }, [prefersReducedMotion]);
  
  useEffect(() => {
    // Skip mouse effects if reduced motion is preferred
    if (prefersReducedMotion) return;
    
    // Throttle mouse events for better performance
    let timeoutId: NodeJS.Timeout;
    const throttledMouseMove = (e: MouseEvent) => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        handleMouseMove(e);
        timeoutId = null as any;
      }, 16); // ~60fps
    };
    
    window.addEventListener('mousemove', throttledMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', throttledMouseMove);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [handleMouseMove, prefersReducedMotion]);

  return (
    <section 
      ref={heroRef}
      className={cn(
        "hero-section relative w-full h-screen lg:h-[600px] xl:h-[700px] lg:pt-32 lg:pb-20 sm:pt-40 sm:pb-28 overflow-hidden",
        className
      )}
      {...props}
    >
      {/* Full-width banner image with warm overlay */}
      <div className="absolute inset-0 overflow-hidden -z-10">
        {imageUrl && (
          <motion.div
            ref={bannerRef}
            className="absolute inset-0"
            style={{ 
              y: bannerY,
              opacity: opacityProgress
            }}
          >
            {/* Desktop Image */}
            <Image 
              src={imageUrl}
              alt="Industry Solutions Banner"
              fill
              className="hidden lg:block object-cover"
              sizes="(min-width: 1024px) 100vw, 0px"
              priority
              quality={85}
              placeholder="blur"
              blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R+Rw="
            />
            {/* Mobile Image */}
            {mobileImageUrl && (
              <Image 
                src={mobileImageUrl}
                alt="Industry Solutions Banner"
                fill
                className="lg:hidden object-cover"
                sizes="(max-width: 1023px) 100vw, 0px"
                priority
                quality={85}
                placeholder="blur"
                blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R+Rw="
              />
            )}
          </motion.div>
        )}
        {/* Warm, branded gradient overlay for hero section */}
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-100/60 via-orange-100/30 to-transparent dark:from-gray-950/80 dark:via-gray-900/60 dark:to-transparent pointer-events-none before:absolute before:inset-0 before:pointer-events-none dark:before:bg-[radial-gradient(ellipse_at_75%_60%,rgba(253,224,71,0.22)_0%,rgba(251,191,36,0.10)_40%,rgba(17,24,39,0.85)_100%)]" />
        
        {/* Subtle accent glows */}
        
      </div>

      {/* Mobile/Tablet Content overlay */}
      <div className="absolute inset-0 z-10 p-6 pt-24 sm:p-12 md:p-16 lg:hidden pointer-events-none">
        <div className="w-full h-full flex flex-col justify-between">
          {/* Top Content: Badge + Title (Mobile/Tablet only) */}
          <div className="flex-shrink-0">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="mb-4"
            >
              <Badge
                variant="premium"
                size="lg"
                radius="full"
                className="pointer-events-auto"
              >
                Industries We Serve
              </Badge>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <Heading
                level={1}
                align="left"
                className="text-3xl md:text-5xl max-w-2xl"
                style="gradient"              
              >
                {title}
              </Heading>
            </motion.div>
          </div>

          {/* Bottom Content: CTA Buttons (Mobile/Tablet only) */}
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 pointer-events-auto flex-shrink-0"
          >
            <Link href={primaryCtaLink} className="w-full sm:w-auto">
              <Button 
                variant="primary" 
                size="lg" 
                className="group relative w-full whitespace-nowrap overflow-hidden rounded-full"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent transition-transform duration-500 ease-in-out -translate-x-full group-hover:translate-x-0" />
                <span className="relative">{primaryCta}</span>
                <motion.div
                  className="relative ml-2"
                  whileHover={{ x: 4 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <ArrowRight className="h-4 w-4" />
                </motion.div>
              </Button>
            </Link>
            <Link href={secondaryCtaLink} className="w-full sm:w-auto">
              <Button 
                variant="secondary" 
                size="lg" 
                className="w-full whitespace-nowrap rounded-full"
              >
                {secondaryCta}
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Desktop Content - Original Container Layout */}
      <div className="hidden lg:block container mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left content panel - spans 6 columns */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 z-20 relative hero-content"
            style={prefersReducedMotion ? undefined : { 
              transform: `translate3d(${-mousePosition.x * 0.5}px, ${-mousePosition.y * 0.5}px, 0)`,
              willChange: 'transform'
            }}
          >
            {/* Label with subtle animation */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <Badge
                variant="premium"
                size="lg"
                radius="full"
                className="mb-4"
              >
                Industries We Serve
              </Badge>
            </motion.div>
            
            {/* Title with enhanced typography and animation */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
            <Heading
              level={1}
              align="left"
              className="mb-3 text-5xl"
              style="gradient"              
            >
                {title}
              </Heading>
            </motion.div>
            
            {/* Description with enhanced typography */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.7 }}
            >
              <Text 
                variant="body-large"                
                className="text-lg xl:text-xl mb-8 max-w-xl font-normal leading-relaxed text-gray-700 dark:text-gray-100"
              >
                {description}
              </Text>
            </motion.div>
            
            {/* Features - Redesigned as elegant pills with subtle hover effects */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-wrap gap-3 mb-8"
            >
              {features.map((feature, index) => (
                <Badge
                  key={`${feature.text}-${index}`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + (index * 0.1), duration: 0.5 }}
                  whileHover={prefersReducedMotion ? undefined : { y: -3, scale: 1.03 }}
                  whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}
                  variant="secondary"
                  radius="full"
                  className="px-5 py-2.5 shadow border-transparent font-medium text-sm"
                >
                  {feature.icon}
                  {feature.text}
                </Badge>
              ))}
            </motion.div>
            
            {/* CTA buttons with original styling */}
            <motion.div 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="flex flex-col sm:flex-row gap-4 mt-6"
            >
              <Link href={primaryCtaLink} className="w-full sm:w-auto">
                <Button 
                  variant="primary" 
                  size="lg" 
                  className="w-full whitespace-nowrap rounded-full group"
                  endIcon={<ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                >
                  {primaryCta}
                </Button>
              </Link>
              <Link href={secondaryCtaLink} className="w-full sm:w-auto">
                <Button 
                  variant="secondary" 
                  size="lg" 
                  className="w-full whitespace-nowrap rounded-full"
                >
                  {secondaryCta}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
          
          {/* Right side - intentionally left empty to showcase the banner image */}
          <div className="lg:col-span-6"></div>
        </div>
      </div>
      
      {/* Floating information card - now uses design system Card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.7 }}
        className="absolute bottom-8 right-8 z-30 max-w-[250px] hidden lg:block"
        aria-label="Industry Certification Card"
      >
        <Card
          variant="elevated"
          radius="md"
          elevation="lg"
          className="w-full min-h-[56px] bg-white/95 dark:bg-[#23272f] border border-gray-200/80 dark:border-none shadow-xl dark:shadow-lg backdrop-blur-sm"
        >
          <CardHeader className="flex flex-row items-center gap-3 py-2 px-3 min-h-0">
            <span className="inline-flex items-center justify-center bg-primary-100 dark:bg-primary-500/10 rounded-lg h-10 w-10">
              <ShieldCheck className="h-5 w-5 text-primary-600 dark:text-gray-200" aria-hidden="true" fill="none" stroke="currentColor" />
            </span>
            <div className="flex flex-col justify-center">
              <CardTitle className="text-base font-semibold text-gray-900 dark:text-white m-0">Industry Certified</CardTitle>
              <CardDescription className="text-xs text-gray-600 dark:text-gray-300 m-0">Premium Quality Standards</CardDescription>
            </div>
          </CardHeader>
        </Card>
      </motion.div>
      
      {/* Hidden text for screen readers to improve accessibility */}
      <span className="sr-only">
        Industry hero banner showing premium packaging solutions. The image features our high-quality products with elegant styling.
      </span>
    </section>
  );
}